import type {
    Machine,
    MachineEntry,
    MachineEvent,
    MachineEventObject,
    MachineSnapshot,
    MachineStateValue,
} from './state-machine';
import {cloneSerializable, isPromiseLike} from '../utils';

type TransitionEventCreator = (...args: never[]) => MachineEvent;
type TransitionSend<
    TSends extends Record<string, TransitionEventCreator> = Record<string, TransitionEventCreator>,
> = ((event: MachineEvent) => void) & (
    string extends keyof TSends
        ? {[name: string]: ((...args: unknown[]) => void) | undefined}
        : {[TName in keyof TSends]: (...args: Parameters<TSends[TName]>) => void}
);

type TransitionHookDefinition<
    TContext,
    TProps,
    TData,
    TName extends string = string,
    TSends extends Record<string, TransitionEventCreator> = Record<string, TransitionEventCreator>,
> = Readonly<{
    name: TName;
    state: string;
    onDone: string;
    onError: string;
    required?: boolean;
    sends?: TSends;
    project?: (snapshot: MachineSnapshot<TContext, TProps>) => TData;
    projectEach?: (snapshot: MachineSnapshot<TContext, TProps>) => readonly TData[];
}>;

type AnyTransitionHook<TContext, TProps> = TransitionHookDefinition<TContext, TProps, unknown>;

type TransitionHookShape = Readonly<{
    name: string;
    state: string;
    onDone: string;
    onError: string;
    required?: boolean;
    sends?: Record<string, TransitionEventCreator>;
    project?: (...args: never[]) => unknown;
    projectEach?: (...args: never[]) => readonly unknown[];
}>;

type TransitionContext<
    TProps,
    TData,
    TElement = unknown,
    TSends extends Record<string, TransitionEventCreator> = Record<string, TransitionEventCreator>,
> = Readonly<{
    el: TElement;
    root: TElement;
    from: string;
    to: string;
    event: MachineEventObject;
    props: TProps;
    data: TData;
    send: TransitionSend<TSends>;
    signal: AbortSignal;
}>;

type TransitionCallback<
    TProps,
    TData,
    TElement = unknown,
    TSends extends Record<string, TransitionEventCreator> = Record<string, TransitionEventCreator>,
> = (
    transition: TransitionContext<TProps, TData, TElement, TSends>,
) => unknown | Promise<unknown>;

type HookProjection<THook> = THook extends {project: (snapshot: infer TSnapshot) => infer TData}
    ? {snapshot: TSnapshot; data: TData}
    : THook extends {projectEach: (snapshot: infer TSnapshot) => readonly (infer TData)[]}
        ? {snapshot: TSnapshot; data: TData}
        : never;
type HookSends<THook> = THook extends {
    sends: infer TSends extends Record<string, TransitionEventCreator>;
} ? TSends : Record<never, TransitionEventCreator>;
type TransitionForHook<THook, TElement = unknown> = THook extends unknown
    ? HookProjection<THook> extends {
        snapshot: MachineSnapshot<infer _TContext, infer TProps>;
        data: infer TData;
    } ? TransitionContext<TProps, TData, TElement, HookSends<THook>> : never
    : never;

type TransitionRegistration<TProps, TElement> = {
    name: string;
    el: TElement;
    callback: TransitionCallback<TProps, unknown, TElement>;
};

type TransitionCoordinatorOptions<TContext, TProps, TElement> = {
    machine: Machine<TContext, TProps>;
    hooks: readonly AnyTransitionHook<TContext, TProps>[];
    root?: TElement;
    resolveElement?: TransitionElementResolver<TElement>;
};

type TransitionElementResolver<TElement> = (scope: Readonly<{
    name: string;
    data: unknown;
    root: TElement;
    registrationEl: TElement;
}>) => TElement | null | undefined;

type TransitionCoordinator<TProps, TElement = unknown> = {
    register<TData>(registration: {
        name: string;
        el: TElement;
        callback: TransitionCallback<TProps, TData, TElement>;
    }): () => void;
    destroy(): void;
};

type DefineHook = {
    <const TDefinition extends TransitionHookShape>(definition: TDefinition): TDefinition;
    <
        TContext,
        TProps,
        TData,
        const TName extends string,
        const TSends extends Record<string, TransitionEventCreator> = Record<string, TransitionEventCreator>,
    >(definition: TransitionHookDefinition<TContext, TProps, TData, TName, TSends>): typeof definition;
};

const defineHook = ((definition: TransitionHookShape): TransitionHookShape => {
    if ((definition.project === undefined) === (definition.projectEach === undefined)) {
        throw new Error(`Transition hook ${definition.name} requires exactly one projection.`);
    }
    return Object.freeze(definition);
}) as DefineHook;

const statePaths = (value: MachineStateValue): readonly string[] => (
    typeof value === 'string' ? [value] : value
);

const commonPrefixLength = (left: string, right: string): number => {
    const leftParts = left.split('.');
    const rightParts = right.split('.');
    let index = 0;
    while (leftParts[index] !== undefined && leftParts[index] === rightParts[index]) index += 1;
    return index;
};

const closestStatePath = (value: MachineStateValue, enteredPath: string): string => {
    const paths = statePaths(value);
    return [...paths].sort((left, right) => (
        commonPrefixLength(right, enteredPath) - commonPrefixLength(left, enteredPath)
    ))[0] ?? enteredPath;
};

const createTransitionCoordinator = <TContext, TProps, TElement = unknown>({
    machine,
    hooks,
    root: ownerRoot,
    resolveElement,
}: TransitionCoordinatorOptions<TContext, TProps, TElement>): TransitionCoordinator<TProps, TElement> => {
    const hooksByName = new Map(hooks.map((hook) => [hook.name, hook]));
    const hooksByState = new Map<string, AnyTransitionHook<TContext, TProps>[]>();
    const registrations = new Map<string, TransitionRegistration<TProps, TElement>[]>();
    const activeRuns = new Set<{entry: MachineEntry<TContext, TProps>; controller: AbortController}>();
    let destroyed = false;

    if (hooksByName.size !== hooks.length) throw new Error('Transition hook names must be unique.');
    hooks.forEach((hook) => {
        const grouped = hooksByState.get(hook.state) ?? [];
        const first = grouped[0];
        if (first && (first.onDone !== hook.onDone || first.onError !== hook.onError)) {
            throw new Error(`Transition hooks sharing ${hook.state} must use the same continuations.`);
        }
        grouped.push(hook);
        hooksByState.set(hook.state, grouped);
    });

    const isActive = (entry: MachineEntry<TContext, TProps>): boolean => (
        !destroyed && machine.getSnapshot().generations[entry.path] === entry.generation
    );

    const continuation = (
        entry: MachineEntry<TContext, TProps>,
        type: string,
        detail: Record<string, unknown> = {},
    ): void => {
        machine.send({
            type,
            ...detail,
            __state: entry.path,
            __generation: entry.generation,
        });
    };

    const projections = (
        hook: AnyTransitionHook<TContext, TProps>,
        snapshot: MachineSnapshot<TContext, TProps>,
    ): readonly unknown[] => hook.projectEach?.(snapshot) ?? [hook.project?.(snapshot)];

    const runQueue = (
        entry: MachineEntry<TContext, TProps>,
        hook: AnyTransitionHook<TContext, TProps>,
        data: unknown,
        queue: TransitionRegistration<TProps, TElement>[],
        signal: AbortSignal,
        index = 0,
    ): void | Promise<void> => {
        for (let cursor = index; cursor < queue.length; cursor += 1) {
            if (!isActive(entry)) return;
            const registration = queue[cursor] as TransitionRegistration<TProps, TElement>;
            const root = ownerRoot ?? registration.el;
            const resolved = resolveElement?.({
                name: hook.name,
                data,
                root,
                registrationEl: registration.el,
            });
            if (resolved === null) return;
            const el = resolved ?? registration.el;
            const send = machine.send.bind(machine) as TransitionSend;
            Object.entries(hook.sends ?? {}).forEach(([name, createEvent]) => {
                send[name] = (...args: unknown[]) => machine.send(createEvent(...args as never[]));
            });
            const transition = Object.freeze({
                el,
                root,
                from: closestStatePath(entry.from, entry.path),
                to: entry.path,
                event: Object.freeze({...entry.event}),
                props: cloneSerializable(entry.snapshot.props),
                data: cloneSerializable(data),
                send,
                signal,
            }) as TransitionContext<TProps, unknown, TElement>;
            const result = registration.callback(transition);
            if (isPromiseLike(result)) {
                return Promise.resolve(result).then(() => runQueue(entry, hook, data, queue, signal, cursor + 1));
            }
        }
    };

    const runEntry = (entry: MachineEntry<TContext, TProps>): void => {
        const groupedHooks = hooksByState.get(entry.path);
        if (!groupedHooks) return;
        const results: Array<void | Promise<void>> = [];
        const errors: unknown[] = [];
        const controller = new AbortController();
        const activeRun = {entry, controller};
        activeRuns.add(activeRun);

        groupedHooks.forEach((hook) => {
            const queue = [...registrations.get(hook.name) ?? []];
            if (queue.length === 0) {
                if (hook.required) errors.push(new Error(`Transition hook ${hook.name} requires a registered callback.`));
                return;
            }
            projections(hook, entry.snapshot).forEach((data) => {
                try {
                    results.push(runQueue(entry, hook, data, queue, controller.signal));
                } catch (error) {
                    errors.push(error);
                }
            });
        });

        const first = groupedHooks[0] as AnyTransitionHook<TContext, TProps>;
        const finish = (asyncErrors: unknown[] = []): void => {
            activeRuns.delete(activeRun);
            if (!isActive(entry)) return;
            const failure = [...errors, ...asyncErrors][0];
            if (failure !== undefined) {
                continuation(entry, first.onError, {error: failure});
            } else {
                continuation(entry, first.onDone);
            }
        };
        const pending = results.filter(isPromiseLike);
        if (pending.length === 0) {
            finish();
            return;
        }
        void Promise.allSettled(pending).then((settled) => finish(settled.flatMap((result) => (
            result.status === 'rejected' ? [result.reason] : []
        ))));
    };

    const unsubscribe = machine.subscribeEntries((entry) => {
        runEntry(entry);
    });
    const unsubscribeSnapshot = machine.subscribe(() => {
        activeRuns.forEach((run) => {
            if (isActive(run.entry)) return;
            run.controller.abort();
            activeRuns.delete(run);
        });
    });

    return {
        register<TData>({name, el, callback}: {
            name: string;
            el: TElement;
            callback: TransitionCallback<TProps, TData, TElement>;
        }): () => void {
            if (destroyed) throw new Error('Transition coordinator has been destroyed.');
            if (!hooksByName.has(name)) throw new Error(`Unknown transition hook: ${name}`);

            const registration: TransitionRegistration<TProps, TElement> = {
                name,
                el,
                callback: callback as TransitionCallback<TProps, unknown, TElement>,
            };
            const queue = registrations.get(name) ?? [];
            queue.push(registration);
            registrations.set(name, queue);

            return () => {
                const current = registrations.get(name);
                if (!current) return;
                const index = current.indexOf(registration);
                if (index >= 0) current.splice(index, 1);
                if (current.length === 0) registrations.delete(name);
            };
        },
        destroy(): void {
            if (destroyed) return;
            destroyed = true;
            unsubscribe();
            unsubscribeSnapshot();
            activeRuns.forEach((run) => run.controller.abort());
            activeRuns.clear();
            registrations.clear();
        },
    };
};

export {createTransitionCoordinator, defineHook};
export type {
    AnyTransitionHook,
    TransitionCallback,
    TransitionContext,
    TransitionCoordinator,
    TransitionCoordinatorOptions,
    TransitionEventCreator,
    TransitionForHook,
    TransitionSend,
    TransitionElementResolver,
    TransitionHookDefinition,
};
